import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notice } from "@/components/ui/notice";
import RewardRow from "@/components/schools/rewards/RewardRow";
import RewardForm from "@/components/schools/rewards/RewardForm";
import PrizeFeatureRequest from "@/components/schools/rewards/PrizeFeatureRequest";

/**
 * The one list-and-edit UI for school rewards, classroom rewards and
 * student prizes: rows that open into an inline form, an add button,
 * and an empty state supplied by the page (often suggestions). Once the
 * campaign is finished the list is read-only.
 *
 * scope: "campaign" | "classroom" | "student"
 * removeWarning(reward): optional — extra words for the remove dialog
 */
export default function RewardManager({
  rewards,
  scope,
  createPath,
  locked = false,
  showProgress = false,
  goals,
  emptyState,
  addLabel = "Add a reward",
  removeWarning,
}) {
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);

  return (
    <div className="space-y-3">
      {locked && (
        <Notice tone="info">This campaign is finished, so its rewards are final.</Notice>
      )}

      {rewards.length === 0 && !adding && (locked
        ? <p className="py-6 text-center text-sm text-muted-foreground">None were added before the campaign finished.</p>
        : emptyState?.({ onAdd: () => setAdding(true) }))}

      {rewards.length > 0 && (
        <ul className="space-y-2">
          {rewards.map((reward) =>
            editingId === reward.id ? (
              <li key={reward.id}>
                <RewardForm
                  scope={scope}
                  reward={reward}
                  path={reward.update_path}
                  goals={goals}
                  onDone={() => setEditingId(null)}
                  onCancel={() => setEditingId(null)}
                />
              </li>
            ) : (
              <RewardRow
                key={reward.id}
                reward={reward}
                locked={locked}
                showProgress={showProgress}
                onEdit={() => { setAdding(false); setEditingId(reward.id); }}
                removeWarning={removeWarning?.(reward)}
              />
            )
          )}
        </ul>
      )}

      {!locked && (adding ? (
        <RewardForm scope={scope} path={createPath} goals={goals} onDone={() => setAdding(false)} onCancel={() => setAdding(false)} />
      ) : rewards.length > 0 && (
        <Button variant="outline" className="w-full" onClick={() => { setEditingId(null); setAdding(true); }}>
          <Plus className="h-4 w-4" />
          {addLabel}
        </Button>
      ))}

      {!locked && <div className="pt-2"><PrizeFeatureRequest /></div>}
    </div>
  );
}
